import { useState } from 'react';
import logo from '../../resources/images/reach-logo.svg';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import auth from '../../api/auth';
import USERTYPES from '../../constants/usertypes';
import { useTenantConfig } from '../../context/TenantConfigContext';
import './Navbar.scss';

// Components
import Avatar from '../Avatar/Avatar';

// Icons
import {
  LayoutDashboard,
  Microscope,
  HeartPulse,
  Syringe,
  Paperclip,
  MapPin,
  ClipboardList,
  Database,
  Globe,
  Languages,
  Package,
  GitBranch,
  BarChart3,
  Users,
  BookOpen,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ChevronUp,
  LogOut,
} from 'lucide-react';

const STORAGE_KEY = 'sidebar_collapsed';
const GROUPS_KEY = 'sidebar_groups_collapsed';

const iconMap = {
  'layout-dashboard': LayoutDashboard,
  'microscope': Microscope,
  'heart-pulse': HeartPulse,
  'syringe': Syringe,
  'paperclip': Paperclip,
  'map-pin': MapPin,
  'clipboard-list': ClipboardList,
  'database': Database,
  'globe': Globe,
  'languages': Languages,
  'package': Package,
  'git-branch': GitBranch,
  'book-open': BookOpen,
  'bar-chart-3': BarChart3,
  'users': Users,
};

const navGroups = [
  {
    name: 'Study Content',
    items: [
      { title: 'Studies', icon: 'microscope', path: '/studies' },
      { title: 'Conditions', icon: 'heart-pulse', path: '/conditions' },
      { title: 'Interventions', icon: 'syringe', path: '/interventions' },
      { title: 'Attachments', icon: 'paperclip', path: '/attachments' },
      { title: 'Locations', icon: 'map-pin', path: '/locations' },
      { title: 'Registries', icon: 'clipboard-list', path: '/study-registries' },
    ],
  },
  {
    name: 'Reference Data',
    defaultCollapsed: true,
    items: [
      { title: 'Global Registries', icon: 'globe', path: '/global-registries' },
      { title: 'Languages', icon: 'languages', path: '/languages' },
      { title: 'Commercial Products', icon: 'package', path: '/commercial-products', pageKey: 'commercial-products' },
      { title: 'Development Status', icon: 'git-branch', path: '/development-status', pageKey: 'development-status' },
      { title: 'Glossary', icon: 'book-open', path: '/glossary', pageKey: 'glossary' },
    ],
  },
  {
    name: 'Tools & Admin',
    items: [
      { title: 'Analytics', icon: 'bar-chart-3', path: '/analytics', pageKey: 'analytics' },
      { title: 'Users', icon: 'users', path: '/users', requiredUsertype: 'super_admin' },
    ],
  },
];

const getDefaultGroupState = () => {
  const saved = localStorage.getItem(GROUPS_KEY);
  if (saved) {
    try { return JSON.parse(saved); } catch { /* ignore */ }
  }
  const defaults = {};
  navGroups.forEach(g => {
    if (g.defaultCollapsed) defaults[g.name] = true;
  });
  return defaults;
};

const Navbar = ({
  isAuth,
  setAuth,
  usertype
}) => {
  const history = useNavigate();
  const location = useLocation();
  const { config } = useTenantConfig();
  const disabledPages = config.disabledPages || [];
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [groupsCollapsed, setGroupsCollapsed] = useState(getDefaultGroupState);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const username = localStorage.getItem('username');

  const pathname = window.location.pathname.split('/');
  let client = '';
  if (pathname.length > 1) { client = pathname[1]; }

  const onLogout = () => {
    const client = localStorage.getItem('client');
    auth.logout((res) => {
      setAuth(res);
      history(`/${client}`);
    });
  };

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  const toggleGroup = (groupName) => {
    setGroupsCollapsed(prev => {
      const next = { ...prev, [groupName]: !prev[groupName] };
      localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      return next;
    });
  };

  if (!isAuth) {
    return null;
  }

  const isActive = (path) => {
    const currentPath = location.pathname;
    if (path === `/${client}`) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const renderNavIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    if (!IconComponent) return null;
    return <IconComponent size={18} />;
  };

  return (
    <div className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      <div className="sidebar__logo-area">
        <Link to={`/${client}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img className="sidebar__logo-img" src={logo} alt="Xogene - Sponsor Trial Website Admin"/>
          {!collapsed && (
            <div>
              <div className="sidebar__logo-text">reach</div>
              <div className="sidebar__logo-subtext">BY XOGENE</div>
            </div>
          )}
        </Link>
        {!collapsed && (
          <button className="sidebar__collapse-toggle" onClick={toggleCollapsed} title="Collapse menu">
            <ChevronsLeft size={17} />
          </button>
        )}
      </div>

      {collapsed && (
        <div className="sidebar__expand-wrap">
          <button className="sidebar__expand-toggle" onClick={toggleCollapsed} title="Expand menu">
            <ChevronsRight size={17} />
          </button>
        </div>
      )}

      <nav className="sidebar__nav">
        {/* Dashboard */}
        <Link
          to={`/${client}`}
          className={`sidebar__nav-item${isActive(`/${client}`) ? ' sidebar__nav-item--active' : ''}${collapsed ? ' sidebar__nav-item--collapsed' : ''}`}
          title={collapsed ? 'Dashboard' : undefined}
        >
          <LayoutDashboard size={18} />
          {!collapsed && <span>Dashboard</span>}
        </Link>

        {/* Nav groups */}
        {navGroups.map(group => {
          const isGroupCollapsed = groupsCollapsed[group.name];
          return (
            <div key={group.name}>
              {!collapsed ? (
                <div className="sidebar__group-header" onClick={() => toggleGroup(group.name)}>
                  <span>{group.name}</span>
                  <span className={`sidebar__chevron${isGroupCollapsed ? ' sidebar__chevron--closed' : ' sidebar__chevron--open'}`}>
                    <ChevronDown size={13} />
                  </span>
                </div>
              ) : (
                <div className="sidebar__divider" />
              )}

              {(!isGroupCollapsed || collapsed) && group.items.map(item => {
                if (item.pageKey && disabledPages.includes(item.pageKey)) return null;
                if (item.requiredUsertype && usertype !== USERTYPES[item.requiredUsertype]) return null;
                const fullPath = `/${client}${item.path}`;
                return (
                  <Link
                    key={item.path}
                    to={fullPath}
                    className={`sidebar__nav-item${isActive(fullPath) ? ' sidebar__nav-item--active' : ''}${collapsed ? ' sidebar__nav-item--collapsed' : ''}`}
                    title={collapsed ? item.title : undefined}
                  >
                    {renderNavIcon(item.icon)}
                    {!collapsed && <span>{item.title}</span>}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="sidebar__user-area">
        <div
          className={`sidebar__user-container${collapsed ? ' sidebar__user-container--collapsed' : ''}`}
          onClick={() => setUserMenuOpen(prev => !prev)}
        >
          {username && <Avatar username={username}/>}
          {!collapsed && (
            <>
              <div className="sidebar__user-info">
                {username && <div className="sidebar__user-name">{username}</div>}
                <div className="sidebar__user-role">Portal Admin</div>
              </div>
              <ChevronUp size={15} className="sidebar__user-chevron" />
            </>
          )}
        </div>

        {userMenuOpen && (
          <>
            <div className="sidebar__user-backdrop" onClick={() => setUserMenuOpen(false)} />
            <div className="sidebar__user-popover">
              <div className="sidebar__user-logout" onClick={() => { setUserMenuOpen(false); onLogout(); }}>
                <LogOut size={16} />
                Log out
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Navbar;
